import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Avatar,
  CircularProgress,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import SendIcon from "@material-ui/icons/Send";
import recipientsService from "../../../../services/recipientsService";
import { getTemplateFieldGroups } from "../../../../services/templateV2Service";
import { groupColorService } from "../../../TemplateBuilder/documentEditor/groupColorService";

interface Recipient {
  id: string;
  name: string;
  email: string;
  order?: number;
  createdAt?: string;
}

interface Group {
  id: string;
  name: string;
  color?: string;
}

interface Field {
  id: string;
  title: string;
  type: string;
  bounds: {
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
  };
  fieldGroup?: string;
  fieldGroupColor?: string;
}

interface FieldRecipientAssignmentProps {
  fields: Field[];
  recipients: Recipient[];
  fieldAssignments: Record<string, string>;
  selectedFieldId: string | null;
  templateId?: string; // Optional - will be extracted from URL if not provided
  recipientGroupAssignments?: Record<string, string>; // recipientId -> groupId (single group only)
  onAssignRecipient: (fieldId: string, recipientId: string) => void;
  onRemoveRecipient: (fieldId: string) => void;
  onRecipientGroupAssignmentChange?: (assignments: Record<string, string>) => void;
  onClearSelection: () => void;
  onFieldSelect: (fieldId: string) => void;
  onPublish: () => void;
  onGroupsAvailableChange?: (hasGroups: boolean) => void;
  onGroupsChange?: (groups: any[]) => void;
  isSending?: boolean;
}

const FieldRecipientAssignment: React.FC<FieldRecipientAssignmentProps> = ({
  fields = [],
  recipients = [],
  fieldAssignments = {},
  selectedFieldId,
  templateId: providedTemplateId,
  recipientGroupAssignments = {},
  onAssignRecipient,
  onRemoveRecipient,
  onRecipientGroupAssignmentChange,
  onClearSelection,
  onFieldSelect,
  onPublish,
  onGroupsAvailableChange,
  onGroupsChange,
  isSending = false,
}) => {
  // Get template ID from URL params
  const { id: urlTemplateId } = useParams<{ id: string }>();
  
  // Use provided templateId or extract from URL
  const templateId = providedTemplateId || urlTemplateId;
    const [selectedRecipientId, setSelectedRecipientId] = React.useState<string>("");
  const [selectedGroupForRecipient, setSelectedGroupForRecipient] = React.useState<{ [recipientId: string]: string }>({});
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [selectedRecipientForGroup, setSelectedRecipientForGroup] = useState<string>("");

  const fieldGroupColorMap = React.useMemo(() => {
    const colorMap = new Map<string, string>();
    if (Array.isArray(fields)) {
      fields.forEach((field) => {
        if (field?.fieldGroup && field?.fieldGroupColor && !colorMap.has(field.fieldGroup)) {
          colorMap.set(field.fieldGroup, field.fieldGroupColor);
        }
      });
    }
    return colorMap;
  }, [fields]);

  const selectedField = selectedFieldId ? fields.find(f => f.id === selectedFieldId) : null;
  const assignedRecipient = selectedFieldId && fieldAssignments[selectedFieldId] 
    ? recipients.find(r => r.id === fieldAssignments[selectedFieldId])
    : null;

  // Calculate number of unique groups assigned to recipients
  const getAssignedGroupsCount = () => {
    if (!isLoadingGroups && availableGroups.length > 0) {
      // When groups are available, count unique groups assigned to recipients
      const assignedGroupIds = new Set(Object.values(recipientGroupAssignments || {}));
      console.log("Assigned group IDs:", Array.from(assignedGroupIds));
      console.log("Number of assigned groups:", assignedGroupIds.size);
      return assignedGroupIds.size;
    }
    return 0;
  };

  // Determine if Send button should be enabled
  const isSendEnabled = () => {
    if (!isLoadingGroups && availableGroups.length > 0) {
      // When groups are available, require at least 2 different groups to be assigned
      const assignedCount = getAssignedGroupsCount();
      const enabled = assignedCount >= 2;
      console.log(`Send button enabled: ${enabled} (${assignedCount} groups assigned, need >= 2)`);
      return enabled;
    } else {
      // When no groups, use original logic (at least one field assignment)
      const fieldCount = Object.keys(fieldAssignments).length;
      const enabled = fieldCount > 0;
      console.log(`Send button enabled: ${enabled} (${fieldCount} field assignments, need > 0)`);
      return enabled;
    }
  };



  // Load groups from API
  const loadGroupsFromAPI = async () => {
    if (!templateId) {
      console.error("templateId is required but not available:", {
        providedTemplateId,
        urlTemplateId,
        finalTemplateId: templateId
      });
      return;
    }
    
    try {
      setIsLoadingGroups(true);
      console.log("Loading field groups for template:", templateId);
      const response = await getTemplateFieldGroups(templateId);
      console.log("Full API response:", response);
      console.log("Response status:", response?.status);
      console.log("Response data:", response?.data);
      
      if (response && response.status === 200) {
        let groups: Group[] = [];
        
        // Check different possible response structures
        if (response.data && response.data.success && response.data.data && response.data.data.groups) {
          const groupsObject = response.data.data.groups;
          console.log("Groups object from API (success path):", groupsObject);
          
          // Convert the groups object to an array format expected by the UI
          const sortedGroupNames = Object.keys(groupsObject).sort();
          
          groups = sortedGroupNames.map((groupName, index) => {
            const groupId = `group-${index + 1}-${groupName.toLowerCase().replace(/\s+/g, '-')}`;

            const colorIndex = index % groupColorService.getAllColors().length;
            const persistedColor = fieldGroupColorMap.get(groupName);
            const colorScheme = persistedColor
              ? groupColorService.getColorByPrimaryColor(persistedColor)
              : groupColorService.getColorByIndex(colorIndex);

            console.log(`Group "${groupName}" assigned color (index ${index + 1}, persisted: ${persistedColor ?? "no"}):`, colorScheme.primary);

            return {
              id: groupId,
              name: groupName,
              color: colorScheme.primary,
            };
          });
        } else if (response.data && response.data.groups) {
          // Alternative response structure
          const groupsObject = response.data.groups;
          console.log("Groups object from API (alternative path):", groupsObject);
          
          const sortedGroupNames = Object.keys(groupsObject).sort();
          
          groups = sortedGroupNames.map((groupName, index) => {
            const groupId = `group-${index + 1}-${groupName.toLowerCase().replace(/\s+/g, '-')}`;
            const colorIndex = index % groupColorService.getAllColors().length;
            const persistedColor = fieldGroupColorMap.get(groupName);
            const colorScheme = persistedColor
              ? groupColorService.getColorByPrimaryColor(persistedColor)
              : groupColorService.getColorByIndex(colorIndex);

            return {
              id: groupId,
              name: groupName,
              color: colorScheme.primary,
            };
          });
        } else {
          console.warn("No groups found in API response. Response structure:", {
            hasData: !!response.data,
            hasSuccess: response.data?.success,
            hasDataData: response.data?.data,
            hasGroups: response.data?.data?.groups,
            responseKeys: response.data ? Object.keys(response.data) : [],
            dataKeys: response.data?.data ? Object.keys(response.data.data) : []
          });
          
          // Fallback: Create test groups for development
          if (process.env.NODE_ENV === 'development') {
            console.log("Creating fallback test groups for development");
            const testGroups = ['Signers', 'Reviewers', 'Approvers', 'Observers'];
            groups = testGroups.map((groupName, index) => {
              const groupId = `test-group-${index + 1}-${groupName.toLowerCase()}`;
              const colorIndex = index % groupColorService.getAllColors().length;
              const persistedColor = fieldGroupColorMap.get(groupName);
              const colorScheme = persistedColor
                ? groupColorService.getColorByPrimaryColor(persistedColor)
                : groupColorService.getColorByIndex(colorIndex);

              return {
                id: groupId,
                name: groupName,
                color: colorScheme.primary,
              };
            });
            
            console.log("Created test groups:", groups);
          }
        }
        
        console.log("Processed groups array:", groups);
        setAvailableGroups(groups);
        
        // Notify parent component about groups availability
        if (onGroupsAvailableChange) {
          onGroupsAvailableChange(groups.length > 0);
        }
        
        // Pass groups to parent for field coloring
        if (onGroupsChange) {
          onGroupsChange(groups);
        }
      } else {
        console.error("API call failed:", {
          status: response?.status,
          data: response?.data
        });
        
        // Notify parent that no groups are available
        if (onGroupsAvailableChange) {
          onGroupsAvailableChange(false);
        }
        
        // Pass empty groups array
        if (onGroupsChange) {
          onGroupsChange([]);
        }
      }
    } catch (error) {
      console.error("Error loading field groups:", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      
      // Notify parent that no groups are available due to error
      if (onGroupsAvailableChange) {
        onGroupsAvailableChange(false);
      }
      
      // Pass empty groups array
      if (onGroupsChange) {
        onGroupsChange([]);
      }
    } finally {
      setIsLoadingGroups(false);
    }
  };

  // Load groups when component mounts or templateId changes
  React.useEffect(() => {
    loadGroupsFromAPI();
  }, [templateId]);

  // Re-sync group colors if fields change after initial load (e.g., persisted colors arrive later)
  React.useEffect(() => {
    if (!availableGroups.length) {
      return;
    }

    let hasColorUpdates = false;
  const updatedGroups = availableGroups.map((group) => {
      if (!group?.name) {
        return group;
      }

      const persistedColor = fieldGroupColorMap.get(group.name);
      if (!persistedColor) {
        return group;
      }

      const normalizedScheme = groupColorService.getColorByPrimaryColor(persistedColor);
      if (group.color && group.color.toLowerCase() === normalizedScheme.primary.toLowerCase()) {
        return group;
      }

      hasColorUpdates = true;
      return {
        ...group,
        color: normalizedScheme.primary,
      };
    });

    if (hasColorUpdates) {
      setAvailableGroups(updatedGroups);

      if (onGroupsChange) {
        onGroupsChange(updatedGroups);
      }
    }
  }, [availableGroups, fieldGroupColorMap, onGroupsChange]);

  // Update selectedRecipientId when selectedFieldId changes
  useEffect(() => {
    if (selectedFieldId) {
      setSelectedRecipientId(fieldAssignments[selectedFieldId] || "");
    } else {
      setSelectedRecipientId("");
    }
  }, [selectedFieldId, fieldAssignments]);

  const handleRecipientChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const recipientId = event.target.value as string;
    setSelectedRecipientId(recipientId);
    
    // Automatically assign the recipient to the selected field
    if (selectedFieldId && recipientId) {
      onAssignRecipient(selectedFieldId, recipientId);
    }
  };


  const handleRemoveAssignment = () => {
    if (selectedFieldId) {
      onRemoveRecipient(selectedFieldId);
      setSelectedRecipientId("");
    }
  };

  const getAssignedRecipientName = (fieldId: string) => {
    const recipientId = fieldAssignments[fieldId];
    const recipient = recipients.find(r => r.id === recipientId);
    return recipient ? recipient.name : null;
  };

  // Use the same color service as the field rendering
  const getRecipientColor = (recipientId: string) => {
    return recipientsService.getRecipientColor(recipientId);
  };

  // Group assignment handlers
  const handleGroupSelection = (recipientId: string, groupId: string) => {
    console.log("Assigning group:", groupId, "to recipient:", recipientId);
    
    if (groupId && onRecipientGroupAssignmentChange) {
      const currentAssignment = recipientGroupAssignments?.[recipientId];
      console.log("Current assignment for recipient:", currentAssignment);
      
      // Remove this group from any other user first (groups can't be shared)
      const updatedAssignments = { ...recipientGroupAssignments };
      
      // Find and remove the group from any other recipient
      Object.keys(updatedAssignments).forEach(otherRecipientId => {
        if (otherRecipientId !== recipientId && updatedAssignments[otherRecipientId] === groupId) {
          console.log("Removing group", groupId, "from recipient", otherRecipientId, "to assign to", recipientId);
          delete updatedAssignments[otherRecipientId];
        }
      });
      
      // Assign the group to the current recipient
      updatedAssignments[recipientId] = groupId;
      
      console.log("Updated assignments:", updatedAssignments);
      onRecipientGroupAssignmentChange(updatedAssignments);
    } else {
      console.log("Missing groupId or callback:", { groupId, hasCallback: !!onRecipientGroupAssignmentChange });
    }
    
    // Clear selection after assignment
    setSelectedGroupForRecipient((prev: { [recipientId: string]: string }) => ({
      ...prev,
      [recipientId]: ''
    }));
  };

  const handleRemoveGroupFromRecipient = (recipientId: string) => {
    if (onRecipientGroupAssignmentChange) {
      const updatedAssignments = {
        ...recipientGroupAssignments
      };
      // Remove the group assignment for this recipient
      delete updatedAssignments[recipientId];
      onRecipientGroupAssignmentChange(updatedAssignments);
    }
  };

  return (
    <Box>
      {/* Selected Field Info - Only show when no groups are available */}
      {!isLoadingGroups && availableGroups.length === 0 && selectedField && (
        <Box mb={2}>
          <Typography variant="h6" gutterBottom>
            {/* {fields.filter(f => fieldAssignments[f.id]).length}*/}
            Field Selected 
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body1">
              {selectedField.title}
            </Typography>
            <IconButton 
              size="small" 
              onClick={onClearSelection}
              style={{ marginLeft: 8 }}
            >
              ×
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Selected Field Recipient Assignment - Only show when no groups are available */}
      {!isLoadingGroups && availableGroups.length === 0 && selectedField && (
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Selected Field Recipient
          </Typography>
          
          <Box display="flex" alignItems="center" mb={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Select Recipient</InputLabel>
              <Select
                value={selectedRecipientId}
                onChange={handleRecipientChange}
                label="Select Recipient"
                style={{
                  backgroundColor: selectedRecipientId ? `${getRecipientColor(selectedRecipientId)}20` : 'transparent',
                  borderColor: selectedRecipientId ? getRecipientColor(selectedRecipientId) : undefined,
                }}
                inputProps={{
                  style: {
                    color: selectedRecipientId ? getRecipientColor(selectedRecipientId) : undefined,
                  }
                }}
                renderValue={(value) => {
                  if (!value) return '';
                  const recipient = recipients.find(r => r.id === value);
                  if (!recipient) return '';
                  return (
                    <Box display="flex" alignItems="center">
                      <Box
                        width={12}
                        height={12}
                        borderRadius="50%"
                        bgcolor={getRecipientColor(recipient.id)}
                        style={{ marginRight: 8 }}
                      />
                      {recipient.name}
                    </Box>
                  );
                }}
              >
                {recipients.map((recipient) => (
                  <MenuItem key={recipient.id} value={recipient.id}>
                    <Box display="flex" alignItems="center">
                      <Box
                        width={12}
                        height={12}
                        borderRadius="50%"
                        bgcolor={getRecipientColor(recipient.id)}
                        style={{ marginRight: 8 }}
                      />
                      {recipient.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <IconButton 
              color="secondary" 
              onClick={handleRemoveAssignment}
              disabled={!assignedRecipient}
              style={{ marginLeft: 8 }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>

          {assignedRecipient && (
            <Box display="flex" alignItems="center" mb={2}>
              <Chip
                label={assignedRecipient.name}
                style={{ 
                  backgroundColor: getRecipientColor(assignedRecipient.id),
                  color: 'white'
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Available Recipients List - Only show if no groups are available */}
      {!isLoadingGroups && availableGroups.length === 0 && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          
          <Typography variant="subtitle1" gutterBottom>
            Available Recipients
          </Typography>
          
          <List dense>
            {recipients.map((recipient) => (
              <ListItem key={recipient.id} style={{ paddingLeft: 0 }}>
                <Box
                  width={12}
                  height={12}
                  borderRadius="50%"
                  bgcolor={getRecipientColor(recipient.id)}
                  marginRight={1}
                />
                <ListItemText 
                  primary={recipient.name}
                  secondary={recipient.email}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}

      {/* Fields List with Assignments */}
      {/* <Divider style={{ margin: '16px 0' }} />
      
      <Typography variant="subtitle1" gutterBottom>
        Fields
      </Typography>
      
      <List dense>
        {fields.map((field) => {
          const assignedRecipientName = getAssignedRecipientName(field.id);
          const isSelected = selectedFieldId === field.id;
          
          return (
            <ListItem 
              key={field.id} 
              button
              onClick={() => onFieldSelect(field.id)}
              style={{ 
                backgroundColor: isSelected ? '#f0f8ff' : 'transparent',
                borderRadius: 4,
                marginBottom: 4
              }}
            >
              <ListItemText 
                primary={field.title}
                secondary={`Page ${field.bounds.page}`}
              />
              {assignedRecipientName && (
                <ListItemSecondaryAction>
                  <Chip
                    label={assignedRecipientName}
                    size="small"
                    style={{ 
                      backgroundColor: getRecipientColor(fieldAssignments[field.id]),
                      color: 'white'
                    }}
                  />
                </ListItemSecondaryAction>
              )}
            </ListItem>
          );
        })}
      </List> */}

      {/* Group Assignment Section - Only show if groups are available or loading */}
      {(isLoadingGroups || availableGroups.length > 0) && (
        <Box mt={3}>
          <Typography variant="h6" style={{ marginBottom: 16 }}>
            Group Assignments
          </Typography>
          
          {isLoadingGroups ? (
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading groups...</Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
              {recipients.map(recipient => {
              const assignedGroupId = recipientGroupAssignments?.[recipient.id];
              const assignedGroup = assignedGroupId ? availableGroups.find(g => g.id === assignedGroupId) : null;
              const assignedGroupScheme = assignedGroup?.color
                ? groupColorService.getColorByPrimaryColor(assignedGroup.color)
                : null;
              const avatarBorder = assignedGroupScheme?.primary ?? undefined;
              const avatarTextColor = assignedGroupScheme?.primary ?? getRecipientColor(recipient.id);
              
              return (
                <Paper
                  key={recipient.id}
                  style={{
                    padding: 16,
                    border: '1px solid #e0e0e0',
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    
                      <Typography variant="body2" style={{ fontWeight: 'bold',   color: assignedGroupScheme?.primary ?? assignedGroup?.color, }}>
                        {recipient.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        ({recipient.email})
                      </Typography>
                      {assignedGroup && (
                        <Typography
                          variant="caption"
                          style={{ 
                            marginLeft: 8, 
                            backgroundColor: assignedGroupScheme?.secondary ?? assignedGroup.color + '10',
                            padding: '2px 6px',
                            borderRadius: 8,
                            color: assignedGroupScheme?.primary ?? assignedGroup.color,
                            fontSize: '0.6rem'
                          }}
                        >
                          Assigned
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Assigned Group (Single) */}
                  {assignedGroup && (
                    <Box display="flex" alignItems="center" style={{ marginBottom: 8 }}>
                      <Chip
                        label={assignedGroup.name}
                        size="small"
                        style={{ 
                          backgroundColor: assignedGroupScheme?.secondary ?? assignedGroup.color + '20',
                          border: 'none',
                          color: assignedGroupScheme?.primary ?? assignedGroup.color,
                        }}
                        onDelete={() => handleRemoveGroupFromRecipient(recipient.id)}
                      />
                    </Box>
                  )}
                  
                  {/* Group Selection Dropdown */}
                  <FormControl size="small" style={{ minWidth: 200 }}>
                    <InputLabel>
                      {assignedGroup ? 'Change Group' : 'Assign Group'}
                    </InputLabel>
                    <Select
                      value={selectedGroupForRecipient[recipient.id] || ''}
                      id="group-selection"
                      onChange={(e) => handleGroupSelection(recipient.id, e.target.value as string)}
                    >
                      <MenuItem value="">
                        <em>{assignedGroup ? 'Change to...' : 'Select a group...'}</em>
                      </MenuItem>
                      {(() => {
                        // Get all groups assigned to OTHER recipients (not current one)
                        const otherRecipientsAssignments = Object.entries(recipientGroupAssignments || {})
                          .filter(([recipId, groupId]) => recipId !== recipient.id && !!groupId)
                          .map(([recipId, groupId]) => groupId);
                        
                        // Show groups that are not assigned to other users
                        const filteredGroups = availableGroups.filter(group => 
                          !otherRecipientsAssignments.includes(group.id)
                        );
                        
                        console.log("Available groups for dropdown:", availableGroups);
                        console.log("Other recipients' assigned groups:", otherRecipientsAssignments);
                        console.log("Assigned group for current recipient:", assignedGroupId);
                        console.log("Filtered groups for dropdown:", filteredGroups);
                        
                        if (filteredGroups.length === 0) {
                          return (
                            <MenuItem disabled>
                              <em>
                                {availableGroups.length === 0 
                                  ? "No groups available" 
                                  : "All groups are already assigned to other users"
                                }
                              </em>
                            </MenuItem>
                          );
                        }
                        
                        return filteredGroups.map(group => (
                          <MenuItem key={group.id} value={group.id}>
                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                              <Box
                                style={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: group.color,
                                }}
                              />
                              {group.name}
                            </Box>
                          </MenuItem>
                        ));
                      })()}
                    </Select>
                  </FormControl>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>
      )}

      {/* No Groups Available Message */}
      {/*!isLoadingGroups && availableGroups.length === 0 && templateId && (
        <Box mt={2} p={2} style={{ backgroundColor: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 4 }}>
          <Typography variant="body2" color="textSecondary">
            No groups are available for this template. You should assign the user to a field manually.
          </Typography>
        </Box>
      )}
        
      {/* Send Button */}
      <Box mt={3}>
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            onClick={onPublish}
            disabled={!isSendEnabled() || isSending}
          >
            {isSending ? "Sending..." : "Send"}
          </Button>
        </Box>
        
        {/* Helper text for Send button requirements */}
        {!isSendEnabled() && !isSending && (
          <Box mt={1} display="flex" justifyContent="flex-end">
            <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.75rem' }}>
              {!isLoadingGroups && availableGroups.length > 0
                ? `Assign at least 2 different groups to enable sending (currently ${getAssignedGroupsCount()} assigned)`
                : 'Assign at least one field to a recipient to enable sending'
              }
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FieldRecipientAssignment;
